
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, OnDestroy, inject } from "@angular/core";
import { TaskService } from "../task.service";
import { select, Store } from "@ngrx/store";
import { LoadTasks, LoadTasksSuccess, getIsTasksNextPageLoading, getIsTasksIsMoreTasks, getTasksCurrentPageOffset, NextLoadTasks, NextLoadTasksSuccess, getSelectedFilterOption, getIsSearchMode, getSortBy, LoadTasksFail, NextLoadTasksFail, StoreProjectList, StorePriorityList, StoreMemberList, CreateTask, CreateTaskSuccess, CreateTaskFail, RemoveTasksSuccess, RemoveTasksFail, UpdateTask, UpdateTasksSuccess, UpdateTaskFail, UpdateTaskSuccess, StoreAuthUser, getAuthUser, RemoveTasks, AllowLoadMore, CreateTasksSuccess, SetTaskStatisticsInfo, SetIsLoading, getSelectedFilterType, StoreTagList, getTagList, SetSearchStatisticsInfo, SetTagStatisticsInfo, UpdateReminderTasks, getReminderTasks, StoreFolderList, getFolderList, SetListStatisticsInfo, StoreMyTeamUsers, StoreSaveSearches, StoreLocationList, SetLocationStatisticsInfo, getLocationList, getAssignmentTasks, UpdateAssignmentTasks, getMemberList, SetTaskDetailHighlight, getTasks, SetTaskFilterType, SetTaskFilterOption, StoreSettings, getTasksById, getTaskStatisticsInfo, SetSelectAll, ResetSelectedTaskId, SetDetailView, SetEditTaskView } from "../store";
import { Task } from "../models/task";
import { TaskAttachment } from "../models/task-attachment";
import { TaskComment } from "../models/task-comment";
import { TasksRootState } from "../store/reducers/index";
import { TaskUtils } from "../shared/task-utils";
import { ErrorType, SuccessType, DateFilterType, BulkUpdateIssueType } from "../shared/task-enum";
import { TasksConstants } from "../shared/task-constacts";
import { Observable , of } from "rxjs";
import { SuccessService } from "../../common/providers/success-service";
import { ErrorService } from "../../common/providers/error-service";
import { TranslateService } from "@ngx-translate/core";
import { BulkUpdateArgs, AuthUser, User, FileUpload, TaskStatistics, Project, ICompact, MyUser, PendingOperation } from "../models/index";
import { ConfigService } from "../../common/providers/config.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { MessageTranslatorService } from "../services/message-translator-service";
import { LocalStorageService } from "../services/local-storage.service";
import { Subject, BehaviorSubject } from "rxjs";
import { DatabaseService } from "../services/database.service";
import { FilesStorageService } from "../services/files-storage.service";
import { CableService } from "../services/cable.service";
import { CommonUtil, MediaType, mobileonly } from "../../common/utils/common.utils";
import { AppConstants } from "../../common/utils/app-constants";
import { environment } from "../../../environments/environment";
import { IsDatabaseReady, getIsDeviceReady, getFederatedApps, getUserProfile, getAllContacts } from "../../reducers";
import { TaskNotificationType } from "../components/task-notifications";
import * as _ from "lodash";
import { getOnlineStatus, getLoginStatus } from "../../reducers";
import { Logout } from "../../actions/app";
import { List } from "../models/list";
import { VncLibraryService } from "@uxf/vnc-library";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "../models/location";
import { taskAdapter } from "../store/reducers/tasks.reducers";
import { takeUntil, distinctUntilChanged, debounceTime, take, filter, tap, delay, catchError, switchMap, finalize, takeWhile, map } from "rxjs/operators";
import { TaskWatchersComponent } from "../components";
import { DomSanitizer } from "@angular/platform-browser";
import { ElectronService } from "../services/electron.service";
import { ContactInfo } from "src/app/shared/models/contact-info.model";
import { AddVNCContacts, BulkLoadVNCContacts } from "src/app/actions/contact.action";
import { StorageService } from "../../common/services/storage.service";

import { TaskRepositoryQueryLayer } from "./task.repository-query.layer";
import { SearchItem } from "./task.repository-state";
import { TaskRepositoryStateBase } from "./task.repository-state";

// Mechanical decomposition layer of TaskRepository (Q-02). Behavior-preserving:
// fields/methods are unchanged, split across inheritance layers; the DI token
// remains the final TaskRepository class.
export abstract class TaskRepositoryOpsLayer extends TaskRepositoryQueryLayer {
  /** True while replayPendingBulkOperations is walking the queue. See the guard there. */
  private isReplayingPendingBulk = false;

  public errorCommentRequired() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.ENTER_A_COMMENT });
  }

  public errorProjectLinkTaskRequired() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.PROJECT_LINKED_TASK_REQUIRED });
  }

  public errorEstimateIsInvalid() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.TASK_ESTIMATE_IS_INVALID });
  }

  public errorEstimateMinuteIsInvalid() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.TASK_ESTIMATE_MINUTE_IS_INVALID });
  }

  public errorProjectRequired() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.PROJECT_REQUIRED });
  }

  public errorDueDateInvalid() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.TASK_DUEDATE_IS_INVALID });
  }

  public getDateFilterItems(type: DateFilterType): any {
    let msg_no_date;
    switch (type) {
      case DateFilterType.StartDate:
        msg_no_date = this.msgs.NO_STARTDATE;
        break;
      case DateFilterType.DueDate:
        msg_no_date = this.msgs.NO_DUEDATE;
        break;
    }
    return [
      {
        date: TaskUtils.getDateAfterOffset(0),
        name: this.msgs.TODAY_DUE
      },
      {
        date: TaskUtils.getDateAfterOffset(1),
        name: this.msgs.TOMORROW_DUE
      },
      {
        date: TaskUtils.getDateAfterOffset(2),
        name: this.msgs[TaskUtils.getWeekDay(TaskUtils.getDateAfterOffset(2).getDay())]
      },
      {
        date: TaskUtils.getDateAfterOffset(3),
        name: this.msgs[TaskUtils.getWeekDay(TaskUtils.getDateAfterOffset(3).getDay())]
      },
      {
        date: null,
        name: msg_no_date
      }
    ];
  }


  public getRepeatItems(): any {
    return [
      {
        value: "d",
        name: this.msgs.TASK_EVERY_DAY
      },
      {
        value: "w",
        name: this.msgs.TASK_EVERY_WEEK
      },
      {
        value: "m",
        name: this.msgs.TASK_EVERY_MONTH
      },
      {
        value: "y",
        name: this.msgs.TASK_EVERY_YEAR
      },
      {
        value: "n",
        name: this.msgs.TASK_NO_REPEAT
      }
    ];
  }


  public syncMyTeamUsers() {
    console.log("[task.repository] syncMyTeamUser");

    // got users from local storage
    const users = this.localStorageService.getMyTeamUsers();
    if (users) {
      this.store.dispatch(new StoreMyTeamUsers(users));
    }

    // sync users from server
    if (this.isOnline) {
      this.taskService.getMyTeamUsers().subscribe( users => {
        this.localStorageService.storeMyTeamUsers(users);
        this.store.dispatch(new StoreMyTeamUsers(users));
        this.successService.emit({ id: SuccessType.TeamUserListSuccess, messages: "" });
      }, err => {
        this.errorService.emit({ id: ErrorType.TeamUserListError, messages: err });
      });
    }
  }

  public archiveUsers(teamUserIds: number[]) {
    this.taskService.archiveUsers(teamUserIds).subscribe( res => {
      this.successService.emit({ id: SuccessType.ArchiveUserSuccess, messages: this.msgs.USER_ARCHIVED });
    }, err => {
      this.errorService.emit({ id: ErrorType.ArchiveUserError, messages: err });
    });
  }

  public unArchiveUsers(teamUserIds: number[]) {
    this.taskService.unArchiveUsers(teamUserIds).subscribe( res => {
      this.successService.emit({ id: SuccessType.UnArchiveUserSuccess, messages: this.msgs.USER_UNARCHIVED });
    }, err => {
      this.errorService.emit({ id: ErrorType.UnArchiveUserError, messages: err });
    });
  }

  public inviteTeamUsers(emailAddresses: string[]) {
    this.taskService.inviteTeamUsers(emailAddresses).subscribe( res => {
      this.successService.emit({ id: SuccessType.InviteTeamUsersSuccess, messages: this.msgs.INVITATION_SENT });
    }, err => {
      this.errorService.emit({ id: ErrorType.InviteTeamUsersError, messages: err });
    });
  }

  public logout() {
    this.removeApiKey();
    this.cleanCacheData();
    this.broadcaster.broadcast("closeAllMdlDialogs");
    this.store.dispatch(new Logout());
    this.broadcaster.broadcast("USER_LOGGED_OUT", true);
    if (this.isCordovaOrElectron) {
      this.taskService.cordovaLogout().subscribe();
      StorageService.removeLocalItem("token");
      this.router.navigate(["/task", TasksConstants.ROUTE_OPEN]);
      this.configService.loginIframe();
    } else {
      StorageService.removeLocalItem("token");
      this.taskService.logout();
    }
  }

  public createTableIfNotExists() {
    if (CommonUtil.isSQLSupported()){
      this.databaseService.createTableIfNotExist();
    }
  }

  public cleanCacheData() {
    this.configService.clearCache();
    this.taskService.clearHeaders();
    // drop db for mobile
    if (CommonUtil.isSQLSupported()){
      this.databaseService.cleanAllData();
    }
    this.localStorageService.cleanLocalStorage();
  }

  public updateMembersAvatars(authUser: AuthUser) {
    if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.getAllProjects().subscribe((projects) => {
          if (projects && projects.length > 0) {
            let allMembersHash = {};
            projects.forEach( project => {
              this.databaseService.getProjectMembers(project.id).subscribe((members) => {
                if (members && members.length > 0) {
                  members.forEach( member => {
                    if (member.id === authUser.id) {
                      member.avatar = authUser.userAvatar;
                    }
                  });
                  allMembersHash[project.id] = members;
                }
              });
            });
            this.databaseService.createOrUpdateProjects(projects, allMembersHash).subscribe((success) => {
              console.log("[task.repository] updateMembersAvatars", success);
            });
          }
        });
      });
    } else {
      // Change members avatars
      let projects = this.localStorageService.getProjects();
      if ( projects && projects.length > 0) {
        projects.forEach( project => {
          let members = this.localStorageService.getMemberList(project.id);
          if ( members && members.length > 0) {
              members.forEach( member => {
                if (member.id === authUser.id) {
                  member.avatar = authUser.userAvatar;
                }
              });
              this.localStorageService.storeMemberList(project.id, members);
          }
        });
      }
    }
  }


  public saveSettings(user: AuthUser) {
    this.taskService.saveSettings(user).subscribe( authUser => {
      this.poppulateAuthUserAvatar(authUser, false).subscribe(success => {
        if (success) {
          console.log("[task.repository][syncAuthUser] set avatar for user. Not storing to cache");
          this.localStorageService.storeUser(authUser);
          if (environment.isElectron) {
            if (authUser.userAvatar) {
              if (CommonUtil.isFileSystemUrl(authUser.userAvatar.toString())) {
                authUser.userAvatar = this.sanitizer.bypassSecurityTrustUrl(authUser.userAvatar);
              }
            }
          }
          this.store.dispatch(new StoreAuthUser(authUser));
        }
      });
      this.successService.emit({ id: SuccessType.SaveSettingSuccess, messages: this.msgs.SETTINGS_SAVE_SUCCESS });
    }, err => {
      this.errorService.emit({ id: ErrorType.SaveSettingError, messages: err });
    });
  }

  public updateTaskInStoreAndDB(task: Task) {
    let tasks = [ task ];
    if (CommonUtil.isSQLSupported()) {
      this.databaseService.updateTask(task, []).subscribe((dbRes) => {
        this.store.dispatch(new UpdateTaskSuccess({ task: { id: tasks[0].id, changes: tasks[0] } }));
      });
    } else {
      this.store.dispatch(new UpdateTaskSuccess({ task: { id: tasks[0].id, changes: tasks[0] } }));
    }
  }

  public saveApiKey() {
    if (environment.isCordova) {
      const apiKey = StorageService.getLocalItem("token");
      console.log("[task.repository][saveApiKey] apiKey", apiKey);
      if (!apiKey) {
        return;
      }
      plugins.appPreferences.store(() => {
        console.log("[task.repository][saveApiKey] success");
      }, () => {
        console.error("[task.repository][saveApiKey] failure");
      }, "redmine-api-key", apiKey);
    }
  }

  public removeApiKey(){
    if (environment.isCordova) {
      plugins.appPreferences.remove(() => {
        console.log("[task.repository][removeApiKey] success");
      }, () => {
        console.log("[task.repository][removeApiKey] failure");
      }, "redmine-api-key");
    }
  }

  public saveApiUrl() {
    if (environment.isCordova) {
      const apiUrl = this.configService.API_URL + "/api/task";
      console.log("[task.repository][saveApiUrl] apiUrl", apiUrl);
      plugins.appPreferences.store(() => {
        console.log("[task.repository][saveApiUrl] success");
      }, () => {
        console.log("[task.repository][saveApiUrl] failure");
      }, "apiUrl", apiUrl);
    }
  }

  public removeApiUrl() {
    if (environment.isCordova) {
      plugins.appPreferences.remove(() => {
        console.log("[task.repository][removeApiUrl] success");
      }, () => {
        console.log("[task.repository][removeApiUrl] failure");
      }, "apiUrl");
    }
  }


  public openTaskExternal(taskId) {
    if (taskId && StorageService.getLocalItem("REDMINE_URL")) {
      let a = document.createElement("a");
      document.body.appendChild(a);
      a.setAttribute("class", "open-new-window");
      a.setAttribute("style", "display: none");
      if (taskId.includes("meta_roadmaps")) {
        a.href = StorageService.getLocalItem("REDMINE_URL") + "/" + taskId;
      } else {
        a.href = StorageService.getLocalItem("REDMINE_URL") + "/issues/" + taskId;
      }
      a.click();
      a.remove();
    }
  }



  public syncTaskInStore(notification) {
    if (notification) {
      if (notification.type === TaskNotificationType.ASSIGNMENT) {
        if (notification.task_id) {
          this.taskService.getTaskDetails(notification.task_id).pipe(take(1)).subscribe(task => {
            if (task) {
              this.createTaskOnDBIfNeededAndFireResult(task);

              this.getTasksStats();
              this.getFolderListWithCounters();
              this.getTagsListWithCounters();
              this.getLocationsWithCounters();
            }
          });
        }
      }
      if (notification.type === TaskNotificationType.TASK_UPDATE
        || notification.type === TaskNotificationType.ASSIGNMENT_REMOVED
        || notification.type === TaskNotificationType.TASK_WATCHER
        || notification.type === TaskNotificationType.MENTIONING) {
        if (notification.task_id) {
          this.store.select(state => getTasksById(state, notification.task_id)).pipe(take(1)).subscribe( oldTask => {
            if (oldTask) {
              this.taskService.getTaskDetails(notification.task_id).pipe(take(1)).subscribe(newTask => {
                if (newTask) {
                  this.updateTaskOnDBIfNeededAndFireResult(oldTask, newTask);
                }
              });
            }
         });
        }
      }
      if (notification.type === TaskNotificationType.DELETION) {
        if (notification.task_id) {
          this.deleteTaskOnDBIfNeededAndFireResult([notification.task_id]);
        }
      }
    }
  }

  makeTalkAudioChatVideoOperation(email, action: string, groupAction: string): void {
      const emails: any[] = [];
      emails.push(email);
      this.talkCallAudioChatVideoOperation(action, emails.toString());
  }

  talkCallAudioChatVideoOperation(action: string, target: string, groupName?: string) {
    let talkAppURL: string = "";
    this.store.select(getFederatedApps).pipe(take(1)).subscribe(apps => {
      talkAppURL = apps.filter( ap => ap.name.toLowerCase() === "vnctalk")[0]?.options?.url;
    });
    talkAppURL = this.getURL(talkAppURL);
    if (this.electronService.isElectron) {
      try {
        let nativeHandler = this.electronService.app.getApplicationNameForProtocol("vnctalk://main");
        console.log("appswitcher nativeHandler: ", nativeHandler);
        if (nativeHandler && nativeHandler !== "") {
          talkAppURL = "vnctalk://main/";
        }
      } catch (e) {
        console.error("[common-repository] error: ", e);
      }
    }

    let sendURL;
    if (talkAppURL.endsWith("/")) {
      sendURL = talkAppURL + "talk/trigger?action=" + action + "&target=" + target;
    } else {
      sendURL = talkAppURL + "/talk/trigger?action=" + action + "&target=" + target;
    }
    if (groupName) {
      sendURL += "&groupName=" + groupName;
    }
    if (environment.isCordova) {
      if (device.platform === "iOS") {
        window.open(sendURL, "_system");
      } else if (device.platform === "Android") {
        navigator.app.loadUrl(sendURL, {
          openExternal: true
        });
      }
    } else if (environment.isElectron) {
      this.electronService.openExternalUrl(sendURL);
    } else {
      window.open(sendURL, "_blank");
    }
  }

  getURL(url: string): string {
      let sendURL: string = "";
      if (url.endsWith("/")) {
          sendURL = url;
      } else {
          sendURL = url + "/";
      }
      return sendURL;
  }

  sendEmail(email): void {
    const emails: any[] = [];
    emails.push(email);
    let mailAppURL: string = "";
    this.store.select(getFederatedApps).pipe(take(1)).subscribe(apps => {
        mailAppURL = apps.filter( ap => ap.name.toLowerCase() === "vncmail")[0]?.options.url;
    });
    mailAppURL = this.getURL(mailAppURL);
    const sendURL = mailAppURL + "mail/compose?to=" + emails.toString();
    if (environment.isCordova) {
        if (device.platform === "iOS") {
            window.open(sendURL, "_system");
        } else if (device.platform === "Android") {
            navigator.app.loadUrl(sendURL, {
                openExternal: true
            });
        }
    } else if (environment.isElectron) {
      try {
        let nativeHandler = this.electronService.app.getApplicationNameForProtocol("vncmail://");

        if (nativeHandler && nativeHandler !== "") {
          let nativeLink = "vncmail://" + sendURL.split("mail/")[1];
          console.log("sendEmail mailAppURL nativeLink: ", nativeLink);
          this.electronService.openExternalUrl(nativeLink);
        } else {
          this.electronService.openExternalUrl(CommonUtil.translateAppURLtoDeeplinkURL(sendURL));
        }
      } catch (e) {
        console.error("[AppSwitcherComponent] error: ", e);
      }
    } else {
      window.open(CommonUtil.translateAppURLtoDeeplinkURL(sendURL), "_blank");
    }
  }

  createTicket(email): void {
    let projectAppURL: string = "";
    this.store.select(getFederatedApps).pipe(take(1)).subscribe(apps => {
      projectAppURL = apps.filter( ap => ap.name.toLowerCase() === "vncproject")[0]?.options?.url;
    });
    projectAppURL = this.getURL(projectAppURL);
    const sendURL = projectAppURL + "issues/new?assignee_username=" + email;
    if (environment.isCordova) {
      if (device.platform === "iOS") {
        window.open(sendURL, "_system");
      } else if (device.platform === "Android") {
        navigator.app.loadUrl(sendURL, {
          openExternal: true
        });
      }
    } else if (environment.isElectron) {
      this.electronService.openExternalUrl(sendURL);
    } else {
      window.open(sendURL, "_blank");
    }
  }

  openMail(folderId: any, mailId: any): void {
    // this.store.pipe(select(state => getFolderById(state, folderId)), take(1)).subscribe(f => {
    //   console.log("openMail folder f:", f);
    //   let newLocationPath = "/mail" + f.absFolderPath.toLowerCase() + "/detail/m/" + mailId;
    //   this.router.navigate([newLocationPath]);
    // });
  }

  openTask(taskId: string): void {
    let taskAppURL: string = "";
    this.store.select(getFederatedApps).pipe(take(1)).subscribe(apps => {
      taskAppURL = apps.filter( ap => ap.name.toLowerCase() === "vnctask")[0]?.options?.url;
    });
    taskAppURL = this.getURL(taskAppURL);
    const sendURL = taskAppURL + "redirect?action=open-task&id=" + taskId;
    if (environment.isCordova) {
      if (device.platform === "iOS") {
        window.open(sendURL, "_system");
      } else if (device.platform === "Android") {
        navigator.app.loadUrl(sendURL, {
          openExternal: true
        });
      }
    } else if (environment.isElectron) {
      this.electronService.openExternalUrl(sendURL);
    } else {
      window.open(sendURL, "_blank");
    }
  }

  jumpToChat(contact: ContactInfo, talkId: string, createdDt: string): void {
    let talkAppURL: string = "";
    let timestamp = new Date(createdDt).getTime();
    let target = "";
    if ( contact && contact.jid) {
      target = contact.jid;
    } else {
      if ( contact && contact.emails && contact.emails.length > 0) {
        target = contact.emails[0].email;
      } else {
        // this.toastService.show("CONTACT_MUST_HAVE_EMAIL_ADDRESS");
        return;
      }
    }
    this.store.select(getFederatedApps).pipe(take(1)).subscribe(apps => {
      talkAppURL = apps.filter( ap => ap.name.toLowerCase() === "vnctalk")[0]?.options?.url;
    });
    talkAppURL = this.getURL(talkAppURL);
    try {
      if (environment.isElectron) {
        let nativeHandler = this.electronService.app.getApplicationNameForProtocol("vnctalk://main");
        console.log("appswitcher nativeHandler: ", nativeHandler);
        if (nativeHandler && nativeHandler !== "") {
          talkAppURL = "vnctalk://main/";
        }
      }
    } catch (e) {
      console.error("[common-repository] error: ", e);
    }
    let sendURL;
    if (talkAppURL.endsWith("/")) {
      sendURL = talkAppURL + "talk/trigger?action=jump-to-chat&target=" + target + "&chat_id=" + talkId + "&timestamp=" + timestamp;
    } else {
      sendURL = talkAppURL + "/talk/trigger?action=jump-to-chat&target=" + target + "&chat_id=" + talkId + "&timestamp=" + timestamp;
    }
    if (environment.isCordova) {
      if (device.platform === "iOS") {
        window.open(sendURL, "_system");
      } else if (device.platform === "Android") {
        navigator.app.loadUrl(sendURL, {
          openExternal: true
        });
      }
    } else if (environment.isElectron) {
      this.electronService.openExternalUrl(sendURL);
    } else {
      window.open(sendURL, "_blank");
    }
  }

  createTask(email): void {
    let taskAppURL: string = "";
    this.store.select(getFederatedApps).pipe(take(1)).subscribe(apps => {
      taskAppURL = apps.filter( ap => ap.name.toLowerCase() === "vnctask")[0]?.options?.url;
    });
    taskAppURL = this.getURL(taskAppURL);
    const sendURL = taskAppURL + "task/open?action=compose&assignee=" + email;
    if (environment.isCordova) {
      if (device.platform === "iOS") {
        window.open(sendURL, "_system");
      } else if (device.platform === "Android") {
        navigator.app.loadUrl(sendURL, {
          openExternal: true
        });
      }
    } else if (environment.isElectron) {
      this.electronService.openExternalUrl(sendURL);
    } else {
      window.open(sendURL, "_blank");
    }
  }











  /**
   * The pending* methods below are internals of the offline replay engine
   * (replayPendingBulkOperations / processPendingOperations, STR-06): they are awaited in
   * strict queue order, so they cannot become fire-and-forget effects. The UpdateTask /
   * CreateTask / RemoveTasks dispatches inside them are the loading-flag state writes the
   * reducer expects, not effect triggers - they are out of scope for the STR-07 trigger
   * conversion by design.
   */
  updatePendingBulkTasks(selectedTasks: Task[], fieldToUpdate: BulkUpdateIssueType, args: BulkUpdateArgs): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log("[TaskRepository] updateBulkTasks");

      const tasksIds: number[] = selectedTasks.map(t => t.id);

      let valueToUpdate;
      switch (fieldToUpdate) {
        case BulkUpdateIssueType.StartDate:
        case BulkUpdateIssueType.DueDate:
        case BulkUpdateIssueType.InviteTo:
          valueToUpdate = (args.value != null) ? args.value : "none";
          break;
        case BulkUpdateIssueType.Tags:
          valueToUpdate = [args.name];
          break;
        case BulkUpdateIssueType.Watchers:
          let watchers = JSON.parse(args.value);
          valueToUpdate = [];
          watchers.forEach(member => {
            valueToUpdate.push(member.id);
          });
          break;
        default:
          valueToUpdate = args.id;
      }

      console.log("[TaskRepository] updateBulkTasks. fieldToUpdate: " + fieldToUpdate + ". valuesToUpdate: " + valueToUpdate);

      this.store.dispatch(new UpdateTask());
      selectedTasks.forEach((i) => {
        i.isChecked = false;
      });
      if (this.isOnline) {
        this.taskService.updateBulkTasks(tasksIds, fieldToUpdate, valueToUpdate).pipe(take(1)).subscribe(res => {
          this.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
          this.syncBulkUpdateToDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
          this.FormattedDateForDB = null;
          this.store.dispatch(new SetEditTaskView(false));
          this.store.dispatch(new SetSelectAll(false));
          this.store.dispatch(new ResetSelectedTaskId());
          resolve("done");
        }, err => {
          this.store.dispatch(new SetEditTaskView(false));
          this.store.dispatch(new SetSelectAll(false));
          this.store.dispatch(new ResetSelectedTaskId());
          resolve("");
          console.error("[TaskRepository] updateBulkTasks, error from server: ", err);
          let msg = "";
          switch (fieldToUpdate) {
            case BulkUpdateIssueType.StartDate:
              msg = this.msgs.START_DATE_CHANGE_ERROR_MESSAGE;
              break;
            case BulkUpdateIssueType.DueDate:
              msg = this.msgs.TASK_DUE_DATE_UPDATE_FAIL;
              break;
            default:
              msg = err;
          }
          this.store.dispatch(new UpdateTaskFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: msg });
        });
      } else if (CommonUtil.isSQLSupported()) {
        this.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
      }
      else if (!this.isOnline) {
        const id = CommonUtil.newPendingOperationId();
        const task = {
          id: id,
          body: {
            tasks: selectedTasks,
            fieldToUpdate: fieldToUpdate,
            valueToUpdate: args,
            operationType: BulkUpdateIssueType[fieldToUpdate],
          }
        };
        this.databaseService.addPendingOperationTask([task]).subscribe(updatedTask => {
          this.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
          this.syncBulkUpdateToDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
          resolve("done");
        }, err => {
          resolve(err);
          this.store.dispatch(new UpdateTaskFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
      }
    });
  }

  updatePendingTask(oldTask: Task, newTask: Task, fileUploads: FileUpload[], watcherMembers: User[]): Promise<any> {
    return new Promise((resolve, reject) => {
      this.store.dispatch(new UpdateTask());
      let tasksStore;
      this.store.select(getTasks).subscribe(tasksFromStore => {
        tasksStore = tasksFromStore;
      });
      let removedTagsIds: number[] = [];
      oldTask.tags.forEach(oldTag => {
        let tagRemoved = true;
        for (let newTag of newTask.tags) {
          if (oldTag.id === newTag.id) {
            tagRemoved = false;
            break;
          }
        }
        if (tagRemoved) {
          removedTagsIds.push(oldTag.id);
        }
      });

      if (this.isOnline) {
        this.taskService.updateTask(oldTask, newTask, fileUploads, watcherMembers).pipe(take(1)).subscribe(serverRes => {
          if (CommonUtil.isSQLSupported()) {
            if (fileUploads && fileUploads.length > 0) {
              this.taskService.getTaskDetails(oldTask.id).subscribe(updatedTaskFromServer => {
                this.databaseService.updateTask(updatedTaskFromServer, removedTagsIds).subscribe((dbRes) => {
                  this.updateTaskSuccess(oldTask, updatedTaskFromServer);
                });
              });
            } else {
              this.databaseService.updateTask(serverRes, removedTagsIds).subscribe((dbRes) => {
                this.updateTaskSuccess(oldTask, serverRes);
              });
            }
          } else {
            this.store.dispatch(new SetEditTaskView(false));
            this.store.dispatch(new SetSelectAll(false));
            this.store.dispatch(new ResetSelectedTaskId());
            resolve("done");
            this.updateTaskSuccess(oldTask, serverRes);
            for (let store of TaskRepositoryStateBase.taskStoreList) {
              this.databaseService.getTaskById(oldTask.id, store).subscribe(t => {
                if (t) {
                  newTask.isChecked = false;
                  this.databaseService.addBulkTasks([newTask], store);
                }
              });
            }
          }
        }, err => {
          this.store.dispatch(new SetEditTaskView(false));
          this.store.dispatch(new SetSelectAll(false));
          this.store.dispatch(new ResetSelectedTaskId());
          resolve("done");
          this.updateTaskError(oldTask, err);
        });

        // offline task update
      } else if (CommonUtil.isSQLSupported()) {
        // update on DB
        if (watcherMembers && watcherMembers.length > 0) {
          newTask.watchers = watcherMembers;
        }
        this.databaseService.updateTask(newTask, removedTagsIds).subscribe((dbRes) => {
          const fileUploadsValue = fileUploads && fileUploads.length > 0 ? fileUploads : null;
          const repeatValue = newTask.repeat !== "" ? newTask.repeat : null;
          const watcherMembersValue = watcherMembers && watcherMembers.length > 0 ? watcherMembers : null;
          this.databaseService.addPendingTaskUpdate(newTask.id, fileUploadsValue, repeatValue, watcherMembersValue).subscribe(res => {
            this.updateTaskSuccess(oldTask, newTask);
          });
        });
      }
      if (!this.isOnline) {
        newTask.isChecked = false;
        const task = [
          {
            id: oldTask.id,
            body: {
              oldTask: oldTask,
              newTask: newTask,
              fileUploads: fileUploads,
              watcherMembers: watcherMembers,
              operationType: "nonBulkUpdate"
            }
          }
        ];
        this.databaseService.addPendingOperationTask(task).subscribe(r => {
          resolve("done");
          const index = tasksStore.findIndex(item => item.id === oldTask.id);
          tasksStore[index] = r;
          this.databaseService.addAllTasks([newTask]).subscribe(res => {
            this.syncNonBulkToDB(oldTask, newTask, tasksStore, r);
          });
        });
      }
    });
  }

  removePendingTasksQuery(selectedIds: number[]): Promise<any> {
    let successEvent = SuccessType.TasksRemoved;
    console.log("[TaskRepository] removeTasksInternal: ", selectedIds);
    return new Promise((resolve,reject) => {
      this.store.dispatch(new RemoveTasks());

      // remove on server
      if (this.isOnline) {
        this.taskService.removeTasks(selectedIds).pipe(take(1)).subscribe(res => {
          this.store.dispatch(new SetEditTaskView(false));
          this.store.dispatch(new SetSelectAll(false));
          this.store.dispatch(new ResetSelectedTaskId());
          resolve("done");
          // remove on DB
          if (CommonUtil.isSQLSupported()) {
            this.databaseService.removeTasks(selectedIds).subscribe(res => {
              this.removeTasksSuccess(selectedIds, successEvent);
            });
          } else {
            this.removeTasksSuccess(selectedIds, successEvent);
          }
        }, err => {
          // 404
          if (err === "Resource Not Found" && CommonUtil.isSQLSupported()) {
            // remove on DB if not founfd on server
            this.databaseService.removeTasks(selectedIds).subscribe(res => {
              this.removeTasksSuccess(selectedIds, successEvent);
            });
          } else {
            resolve("done");
            this.removeTasksError(err);
          }
        });

        // offline remove
      } else if (CommonUtil.isSQLSupported()) {
        // delete on DB and create a pending operation
        this.databaseService.removeTasks(selectedIds).subscribe(res => {
          // add to pending queue
          selectedIds.forEach(taskId => {
            this.databaseService.addPendingTaskDelete(taskId).subscribe(res => {

            });
          });

          this.removeTasksSuccess(selectedIds, successEvent);
        });
      } else if (!this.isOnline) {
        let taskStats = [];
        this.store.select(getTaskStatisticsInfo).subscribe(info => {
          if (info) {
            taskStats = info;
          }
        });
        const id = CommonUtil.newPendingOperationId();
        const task = {
          id: id,
          body: {
            tasks: selectedIds,
            operationType: "deleteTask",
          }
        };
        this.databaseService.addPendingOperationTask([task]).subscribe(updatedTask => {
          resolve("done");
          selectedIds.forEach((elem) => {
            TaskRepositoryStateBase.taskStoreList.forEach((store) => {
              this.databaseService.getTaskById(elem, store).subscribe(t => {
                if (t) {
                  const currentStoreStat = TaskRepositoryStateBase.statWithStore.find(r => r.store === store);
                  this.removeTasksSuccess(selectedIds, successEvent);
                  this.databaseService.deleteBulkTasks(elem, store);
                  taskStats = taskStats.map(stat => {
                    if (stat.type === currentStoreStat.stat) {
                      stat.info.completed_count = stat.info.completed_count - 1;
                      stat.info.total_count = stat.info.total_count - 1;
                    }
                    return stat;
                  });
                  this.databaseService.addTaskStats(taskStats).subscribe(done => {
                    this.getTasksStats();
                    let routingOption;
                    this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => {
                      const currentStore = TaskRepositoryStateBase.statWithStore.find(r => r.store === store);
                      if (currentStore?.store == routingOption) {
                        this.getDataFromDatabase(routingOption);
                      }
                    });
                  });
                }
              });
            });
          });
        });
      }
    });
  }

  pendingDuplicateTasks(tasks: Task[]): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log("[TaskRepository] duplicateTasks. tasks: ", tasks);

      const tasksPayloadToCreate: any[] = tasks.map(t => this.getTaskCopy(t));

      this.store.dispatch(new CreateTask());

      console.log("[TaskRepository] duplicateTasks. tasksPayloadToCreate: ", tasksPayloadToCreate);

      if (this.isOnline) {
        // create on server
        this.taskService.bulkCreateTasks(tasksPayloadToCreate).pipe(take(1)).subscribe(createdTasks => {
          console.log(" => duplicateTasks: ", createdTasks, tasksPayloadToCreate);
          // create on DB
          if (CommonUtil.isSQLSupported()) {
            this.databaseService.saveOrUpdateTasks(createdTasks).pipe(take(1)).subscribe((res) => {
              this.duplicateTasksSuccess(createdTasks);
            });
          } else {
            this.duplicateTasksSuccess(createdTasks);
            this.databaseService.addNewTasks(createdTasks);
            this.databaseService.addAllTasks(createdTasks);
            this.store.dispatch(new SetEditTaskView(false));
            this.store.dispatch(new SetSelectAll(false));
            this.store.dispatch(new ResetSelectedTaskId());
            resolve(createdTasks);
          }
        }, err => {
          this.duplicateTasksError(err);
          resolve("done");
        });

      } else if (CommonUtil.isSQLSupported()) {
        let dupTasks = [];
        tasksPayloadToCreate.forEach(tp => {
          let copyTask: Task = Task.taskToCreateOffline(tp);
          dupTasks.push(copyTask);
        });

        dupTasks.forEach(taskToCreate => {
          this.databaseService.addNewTask(taskToCreate).subscribe(res => {
            this.databaseService.addPendingTaskCreate(taskToCreate.id).subscribe(res => {
              this.duplicateTasksSuccess(dupTasks);
            });
          });
        });
      }
      if (!this.isOnline) {
        let taskStats = [];
        this.store.select(getTaskStatisticsInfo).subscribe(info => {
          if (info) {
            taskStats = info;
          }
        });
        const id = CommonUtil.newPendingOperationId();
        const task = {
          id: id,
          body: {
            tasks: tasksPayloadToCreate,
            operationType: "duplicateTask",
          }
        };
        this.databaseService.addPendingOperationTask([task]).subscribe(res => {
          let required_statuses = this.getStatusItems();
          tasks.forEach((item) => {
            // The queue key and the placeholder task id used to be the same random
            // number. They are different concerns: the key must not collide, the id
            // must stay numeric. Give each duplicate its own temp id.
            item.id = Task.tempId();
            let newStatus;
            newStatus = required_statuses.find(s => {
              if (s.name.toLowerCase() === "new") {
                return { id: s.id, name: s.name };
              }
            });
            item.isChecked = false;
            item.status = {
              id: newStatus.id,
              name: newStatus.name
            };
            this.databaseService.addNewTasks([item]);
            this.databaseService.addAllTasks([item]);
            newStatus = required_statuses.find(s => {
              if (s.name.toLowerCase() === "in progress") {
                return { id: s.id, name: s.name };
              }
            });
            item.status = {
              id: newStatus.id,
              name: newStatus.name
            };
            this.databaseService.addOpenTasks([item]);

            taskStats = taskStats.map(stat => {
              if (stat.type == "new" || stat.type == "open" || stat.type == "all") {
                stat.info.completed_count = stat.info.completed_count + 1;
                stat.info.total_count = stat.info.total_count + 1;
              }
              return stat;
            });
          });
          this.duplicateTasksSuccess(tasks);
          resolve("done");
          this.databaseService.addTaskStats(taskStats).subscribe(done => {
            this.getTasksStats();
            let routingOption;
            this.store.select(getSelectedFilterOption).pipe(take(1)).subscribe(o => routingOption = o);
            if (routingOption) {
              this.getDataFromDatabase(routingOption);
            }
          });
        });
      }
    });
  }

  updateBulkTasksPendingRecurring(selectedTasks: Task[], type: BulkUpdateIssueType, item: BulkUpdateArgs): Promise<any>{
    return new Promise((resolve, reject) => {
      this.store.dispatch(new UpdateTask());
      const selectedIds: number[] = selectedTasks.map(t => t.id);
      if (this.isOnline) {
        this.taskService.updateBulkTasksRecurring(selectedIds, type, item.value).pipe(take(1)).subscribe(res => {
          this.updateBulkTasksRecurringOnDB(selectedTasks, type, item);
          resolve("done");
        }, err => {
          resolve("done");
          this.store.dispatch(new UpdateTaskFail());
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        });
      } else if (CommonUtil.isSQLSupported()) {
        this.updateBulkTasksRecurringOnDB(selectedTasks, type, item);
      } else if (!this.isOnline) {
        const id = CommonUtil.newPendingOperationId();
        const task = {
          id: id,
          body: {
            tasks: selectedIds,
            fieldToUpdate: type,
            valueToUpdate: item,
            operationType: BulkUpdateIssueType[type],
          }
        };
        this.databaseService.addPendingOperationTask([task]).subscribe(updatedTask => {
          this.updateBulkTasksRecurringOnDB(selectedTasks, type, item);
          resolve("done");
          selectedTasks.forEach((elem) => {
            elem["repeat"] = item.value;
            elem.isChecked = false;
            TaskRepositoryStateBase.taskStoreList.forEach((store) => {
              this.databaseService.getTaskById(elem.id, store).subscribe(t => {
                if (t) {
                  this.databaseService.addBulkTasks([elem], store);
                }
              });
            });
          });
        });
      }

    });
  }


  syncNonBulkToDB(oldTask, newTask, tasksStore, r) {
    this.loadTaskToDb(tasksStore).subscribe(taskFromDB => {
      this.updateTaskSuccess(oldTask, r);
      if (oldTask?.tags?.length) {
        const tagList = oldTask?.tags.map(item => item.name);
        for (let tag of tagList) {
          this.databaseService.getTagsTasks(tag).subscribe(res => {
            if (res) {
              const taskIndex = res.findIndex(item => item.id === oldTask.id);
              res[taskIndex] = newTask;
              if (taskIndex > -1) {
                this.databaseService.addTagsTasks(tag, res).subscribe(r => {
                });
              }
            }
          });
        }
      }
      if (oldTask?.location) {
        const locationName = oldTask?.location?.name;
        if (locationName) {
          this.databaseService.getLocationTasks(locationName).subscribe(res => {
            if (res) {
              const taskIndex = res.findIndex(item => item.id === oldTask.id);
              res[taskIndex] = newTask;
              this.databaseService.addLocationTasks(locationName, res).subscribe(r => {
              });
            }
          });
        }
      }
      if (oldTask?.list) {
        const listName = oldTask?.list?.name;
        if (listName) {
          this.databaseService.getListTasks(listName).subscribe(res => {
            if (res) {
              const taskIndex = res.findIndex(item => item.id === oldTask.id);
              if (taskIndex > -1) {
                res[taskIndex] = newTask;
                this.databaseService.addListTasks(listName, res).subscribe(r => {
                });
              }
            }
          });
        }
      }
      let taskStats = [];
      this.store.select(getTaskStatisticsInfo).subscribe(info => {
        if (info) {
          taskStats = info;
        }
      });
      for (let store of TaskRepositoryStateBase.taskStoreList) {
        this.databaseService.getTaskById(oldTask.id, store).subscribe(t => {
          if (t) {
            this.databaseService.addBulkTasks([newTask], store).subscribe(done => {
              if (newTask.status.name !== oldTask.status.name) {
                if (newTask.status.name.toLowerCase() == "completed" && store === "OpenTaskStore") {
                  this.databaseService.deleteBulkTasks(oldTask.id, store);
                  this.databaseService.addBulkTasks([newTask], "CompletedTaskStore");
                  for (let item of taskStats) {
                    if (item.type == "completed") {
                      item.info.completed_count = item.info.completed_count + 1;
                      item.info.total_count = item.info.total_count + 1;
                    }
                    if (item.type == "open") {
                      item.info.total_count = item.info.total_count - 1;
                    }
                  }
                  this.databaseService.addTaskStats(taskStats).subscribe(done => {
                    this.getTasksStats();
                  });
                }
                if (newTask.status.name.toLowerCase() == "in progress" && store === "CompletedTaskStore") {
                  this.databaseService.deleteBulkTasks(oldTask.id, store);
                  this.databaseService.addBulkTasks([newTask], "OpenTaskStore");
                  for (let item of taskStats) {
                    if (item.type == "completed") {
                      item.info.completed_count = item.info.completed_count - 1;
                      item.info.total_count = item.info.total_count - 1;
                    }
                    if (item.type == "open") {
                      item.info.total_count = item.info.total_count + 1;
                    }
                  }
                  this.databaseService.addTaskStats(taskStats).subscribe(done => {
                    this.getTasksStats();
                  });
                }
              }
            });
          }
        });
      }
    });
  }

  refreshUpdateTask(id):Promise<any> {
    return new Promise((resolve, reject) => {
      this.taskService.getTaskDetails(id).subscribe(res => {
        resolve(res);
      }, err => {
        resolve("");
      });
    });
  }

  startCable() {
    console.log("[task.repo] startCable");
  }

  getAttachmentOnlyOfficeUrl(param: any, isOnlyOffice: boolean = true): Observable<any> {
    // console.log('[task.repository] loadReminderTasks');
    if (isOnlyOffice) {
      return this.taskService.getAttachmentOnlyOfficeUrl(param).pipe(take(1));
    } else {
      return this.taskService.getAttachmentCollaboraUrl(param).pipe(take(1));
    }
  }

  searchDocs(params, searchText): Observable<any[]> {
     return this.taskService.getSearchDocs(params, searchText).pipe(
       map((res: any) => {
      if (!res.response) {
        return [];
      }
      return res.response.docs.map((val: any) => this.mapSearchItem(val));
    })
  );
  }
    protected mapSearchItem(doc) {
      const content = doc.content_txt ? doc.content_txt[0] : "";
      const raw = doc.raw_txt ? doc.raw_txt[0] : "";
      let parsedContent = content;
      const rawTxt = raw.replace(/\\"/ig, "\"").replace(/\\'/ig, "\'");
      if (rawTxt !== "") {
          parsedContent = rawTxt;
      }
      const shortContent = parsedContent;
      let id = doc.id;
      let objectId = doc.id;
      if (doc.type_s === "task") {
          id = doc.task_id_i;
      } else if (doc.type_s === "mail") {
          id = doc.mail_id_i;
          objectId = objectId.replace(`.${doc.mail_id_i}`, "");
      } else if (doc.type_s === "talk") {
          objectId = objectId.replace(`.${doc.owner_s}`, "").replace(`talk.${doc.talk_id_s}.`, "");
      }
      return {
          id: id,
          objectId: objectId,
          talkId: doc.talk_id_s,
          owner: doc.owner_s,
          title: doc.title_s ? doc.title_s.replace(/\\"/ig, "\"").replace(/\\'/ig, "\'") : "",
          contentTxt: content.replace(/\\n/g, "<br />"),
          parsedContent: parsedContent.replace(/\\n/g, "<br />"),
          shortContent: shortContent.replace(/\\n/g, "<br />"),
          chatType: doc.talk_type_s,
          rawTxt: raw,
          createdDt: doc.created_dt,
          modifiedDt: doc.modified_dt,
          from: doc.from_s,
          to: doc.to_ss,
          type: doc.type_s,
          version: doc._version_,
          mailFolderID: doc.mail_folder_id_i,
          unread: doc.mail_unread_b,
          flags: doc.mail_flags_s,
          talk_attachment_s: doc.talk_attachment_s ? doc.talk_attachment_s : undefined
      } as SearchItem;
    }

  /**
   * Replays the IndexedDB bulk-operation queue (duplicate / bulk update / recurring /
   * delete / non-bulk update) that was filled while offline.
   *
   * This used to live in AppComponent, which subscribed to the window "online" event
   * and replayed the queue itself while the repository was independently replaying the
   * SQLite queue and pulling server state. Two replay engines on two queues, unordered
   * against each other and against the pull. It lives here now so the sync layer owns a
   * single ordered entry point, and so the replay calls the repository's pending-* helpers
   * directly instead of reaching through the shell.
   *
   */
  protected replayPendingBulkOperations(): void {
    // A flapping connection fires "online" repeatedly. Without this guard two replays run
    // over the same queue and every operation is applied twice, because a queue entry is
    // only deleted once its request comes back.
    if (this.isReplayingPendingBulk) {
      console.log("[TaskRepository] replayPendingBulkOperations already in progress, skip");
      return;
    }
    this.isReplayingPendingBulk = true;

    this.databaseService.getPendingOperationTask().pipe(take(1)).subscribe(pendingTasks => {
      this.processPendingBulkOperations(pendingTasks)
        .catch(err => {
          console.error("[TaskRepository] replayPendingBulkOperations error", err);
        })
        .then(() => {
          this.isReplayingPendingBulk = false;
        });
    }, err => {
      console.error("[TaskRepository] replayPendingBulkOperations, getPendingOperationTask error", err);
      this.isReplayingPendingBulk = false;
    });
  }

  private async processPendingBulkOperations(pendingTasks): Promise<void> {
    if (!pendingTasks?.length || !navigator.onLine) {
      return;
    }

    // getPendingOperationTask already orders by the persisted position (OFF-02); sorting
    // again is cheap and keeps the ordering guarantee local to the replay.
    const orderedTasks = pendingTasks.slice().sort((a, b) => a.position - b.position);
    console.log("[TaskRepository] replayPendingBulkOperations, ops: ", orderedTasks.length);

    const operationOnDuplicate = {};
    for (const task of orderedTasks) {
      if (task?.body?.operationType === "duplicateTask") {
        let count = 0;
        for (const t of task.body.tasks) {
          const taskComplete = await this.pendingDuplicateTasks([t]);
          if (taskComplete?.length) {
            this.databaseService.deleteBulkTasks(task.id, "NewTaskStore");
            this.databaseService.deleteBulkTasks(task.id, "OpenTaskStore");
            this.databaseService.deleteBulkTasks(task.id, "AllTaskStore");
            this.databaseService.addBulkTasks(taskComplete, "NewTaskStore");
            this.databaseService.addBulkTasks(taskComplete, "OpenTaskStore");
            this.databaseService.addBulkTasks(taskComplete, "AllTaskStore");
            operationOnDuplicate[task.id + count] = taskComplete[0].id;
            count++;
          }
        }
        this.databaseService.deletePendingOperations(task.id);
      }

      else if (["Priority", "AssignedTo", "StartDate", "DueDate", "Location", "List", "Tags", "Watchers"].includes(task?.body?.operationType)) {
        const taskToUpdate = task?.body?.tasks.map(t => {
          if (Object.keys(operationOnDuplicate).includes(t.id.toString())) {
            t.id = operationOnDuplicate[t.id];
          }
          return t;
        });
        await this.updatePendingBulkTasks(taskToUpdate, task?.body?.fieldToUpdate, task?.body?.valueToUpdate);
        this.databaseService.deletePendingOperations(task.id);
        const taskIds = task?.body?.tasks.map(t => t.id);
        for (const t of taskIds) {
          await this.refreshUpdateTask(t);
        }
      }

      else if (task?.body?.operationType === "Recurring") {
        for (const t of task.body.tasks) {
          if (Object.keys(operationOnDuplicate).includes(t.id.toString())) {
            t.id = operationOnDuplicate[t.id];
          }
          await this.updateBulkTasksPendingRecurring([t], task?.body?.fieldToUpdate, task?.body?.valueToUpdate);
        }
        this.databaseService.deletePendingOperations(task.id);
      }

      else if (task?.body?.operationType === "nonBulkUpdate") {
        await this.updatePendingTask(task.body.oldTask, task.body.newTask, task.body.fileUploads, task.body.watcherMembers);
        this.databaseService.deletePendingOperations(task.id);
      }

      else if (task?.body?.operationType === "deleteTask") {
        for (let t of task.body.tasks) {
          if (Object.keys(operationOnDuplicate).includes(t.toString())) {
            t = operationOnDuplicate[t];
          }
          await this.removePendingTasksQuery([t]);
        }
        this.databaseService.deletePendingOperations(task.id);
      }

      else if (task?.body?.operationType === "bulkUpdate") {
        for (const t of task.body.tasks) {
          if (Object.keys(operationOnDuplicate).includes(t.id)) {
            t.id = operationOnDuplicate[t.id];
          }
          const taskComplete = await this.updatePendingBulkTasks([t], task.body.status, task.body.args);
          if (taskComplete !== "done") {
            for (const oldTask of task.body.oldTasksBackUp) {
              this.databaseService.deleteBulkTasks(oldTask.id, "CompletedTaskStore");
              if (oldTask.status.name.toLowerCase() === "new") {
                this.databaseService.addBulkTasks([oldTask], "NewTaskStore");
                this.databaseService.addBulkTasks([oldTask], "OpenTaskStore");
              }
              else if (oldTask.status.name.toLowerCase() === "open") {
                this.databaseService.addBulkTasks([oldTask], "OpenTaskStore");
              }
            }
          }
        }
        this.databaseService.deletePendingOperations(task.id);
      }
    }

    this.store.dispatch(new SetEditTaskView(false));
    this.getTasks();
    this.getTasksStats();
    this.addAllTaskToIndexedDb();
  }

}
